import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Filter, 
  BookOpen, 
  PieChart, 
  Building2,
  Calendar,
  Search,
  FileSpreadsheet,
  ArrowUpCircle,
  ArrowDownCircle,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Users,
  Banknote
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf, downloadCsv } from '../utils/downloadPdf';
import { fmtDate as fmtDateTz, fmtDateTime as fmtDateTimeTz } from '../lib/datetime';
import PayrollSearch from './PayrollSearch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount || 0);
};

const formatDate = (dateStr) => (dateStr ? fmtDateTz(dateStr) : '-');
const formatDateTime = (dateStr) => (dateStr ? fmtDateTimeTz(dateStr) : '-');

const ENTITY_TYPE_LABEL = { cash: 'Caja', bank: 'Banco', wallet: 'Billetera', credit_card: 'Tarjeta Crédito' };
const entityTypeLabel = (t) => ENTITY_TYPE_LABEL[t] || 'Otro';

function TreasuryReports() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab') || 'ledger';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [cashBankId, setCashBankId] = useState('');
  const [conceptType, setConceptType] = useState('');
  const [conceptId, setConceptId] = useState('');
  const [movementType, setMovementType] = useState('');
  const [userId, setUserId] = useState('');
  
  // Masters
  const [cashBanks, setCashBanks] = useState([]);
  const [incomeConcepts, setIncomeConcepts] = useState([]);
  const [expenseConcepts, setExpenseConcepts] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Report Data
  const [ledgerData, setLedgerData] = useState(null);
  const [conceptData, setConceptData] = useState(null);
  const [cashBankData, setCashBankData] = useState(null);
  const [cbMovementsData, setCbMovementsData] = useState(null);
  const [selectedCbForReport, setSelectedCbForReport] = useState('');

  // v3.37.0 — Reporte: FC fiscales de compras pagadas con efectivo
  const [cashFcOpFrom, setCashFcOpFrom] = useState('');
  const [cashFcOpTo, setCashFcOpTo] = useState('');
  const [cashFcFcFrom, setCashFcFcFrom] = useState('');
  const [cashFcFcTo, setCashFcFcTo] = useState('');
  const [cashFcSupplier, setCashFcSupplier] = useState('');
  const [cashFcData, setCashFcData] = useState(null);
  const [cashFcSuppliers, setCashFcSuppliers] = useState([]);
  
  useEffect(() => {
    loadMasters();
  }, []);
  
  const loadMasters = async () => {
    try {
      const [cbRes, icRes, ecRes, usersRes, supRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/treasury/cash-banks`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/income-concepts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/expense-concepts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/suppliers`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setCashBanks(cbRes.data);
      setIncomeConcepts(icRes.data);
      setExpenseConcepts(ecRes.data);
      setUsers(usersRes.data);
      setCashFcSuppliers(Array.isArray(supRes.data) ? supRes.data : (supRes.data?.items || []));
    } catch (err) {
      console.error('Error loading masters:', err);
    }
  };
  
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (cashBankId) params.append('cash_bank_id', cashBankId);
    if (conceptType) params.append('concept_type', conceptType);
    if (conceptId) params.append('concept_id', conceptId);
    if (movementType) params.append('movement_type', movementType);
    if (userId) params.append('user_id', userId);
    return params.toString();
  }, [startDate, endDate, cashBankId, conceptType, conceptId, movementType, userId]);
  
  const loadLedger = async () => {
    setLoading(true);
    try {
      const qs = buildQueryParams();
      const res = await axios.get(`${BACKEND_URL}/api/treasury/reports/ledger?${qs}`, { withCredentials: true });
      setLedgerData(res.data);
    } catch (err) {
      toast.error('Error al cargar Libro Diario');
    } finally {
      setLoading(false);
    }
  };
  
  const loadConceptReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (conceptType) params.append('concept_type', conceptType);
      const res = await axios.get(`${BACKEND_URL}/api/treasury/reports/by-concept?${params.toString()}`, { withCredentials: true });
      setConceptData(res.data);
    } catch (err) {
      toast.error('Error al cargar reporte por concepto');
    } finally {
      setLoading(false);
    }
  };
  
  const loadCashBankReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const res = await axios.get(`${BACKEND_URL}/api/treasury/reports/by-cash-bank?${params.toString()}`, { withCredentials: true });
      setCashBankData(res.data);
    } catch (err) {
      toast.error('Error al cargar reporte por caja/banco');
    } finally {
      setLoading(false);
    }
  };
  
  const loadCbMovements = async () => {
    if (!selectedCbForReport) { toast.error('Seleccione una caja/banco'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const res = await axios.get(`${BACKEND_URL}/api/treasury/reports/cash-bank-movements/${selectedCbForReport}?${params.toString()}`, { withCredentials: true });
      setCbMovementsData(res.data);
    } catch (err) {
      toast.error('Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };
  
  const buildCashFcParams = () => {
    const p = new URLSearchParams();
    if (cashFcOpFrom) p.append('op_date_from', cashFcOpFrom);
    if (cashFcOpTo) p.append('op_date_to', cashFcOpTo);
    if (cashFcFcFrom) p.append('fc_date_from', cashFcFcFrom);
    if (cashFcFcTo) p.append('fc_date_to', cashFcFcTo);
    if (cashFcSupplier) p.append('supplier_id', cashFcSupplier);
    return p.toString();
  };

  const loadCashFcReport = async () => {
    setLoading(true);
    try {
      const qs = buildCashFcParams();
      const res = await axios.get(`${BACKEND_URL}/api/treasury/reports/cash-paid-fiscal-invoices?${qs}`, { withCredentials: true });
      setCashFcData(res.data);
    } catch (err) {
      toast.error('Error al cargar reporte de compras en efectivo');
    } finally {
      setLoading(false);
    }
  };

  const clearCashFcFilters = () => {
    setCashFcOpFrom(''); setCashFcOpTo(''); setCashFcFcFrom(''); setCashFcFcTo(''); setCashFcSupplier('');
  };
  
  const handleSearch = () => {
    if (activeTab === 'ledger') loadLedger();
    else if (activeTab === 'by-concept') loadConceptReport();
    else if (activeTab === 'by-cash-bank') loadCashBankReport();
    else if (activeTab === 'cb-movements') loadCbMovements();
    else if (activeTab === 'cash-fc') loadCashFcReport();
  };
  
  const handleExportPdf = () => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    if (activeTab === 'ledger') {
      if (cashBankId) params.append('cash_bank_id', cashBankId);
      downloadPdf(`/treasury/reports/ledger/pdf?${params.toString()}`, 'Libro_Diario_Tesoreria.pdf', 'Generando PDF del Libro Diario...');
    } else if (activeTab === 'by-concept') {
      downloadPdf(`/treasury/reports/by-concept/pdf?${params.toString()}`, 'Reporte_Por_Concepto.pdf', 'Generando PDF por Concepto...');
    } else if (activeTab === 'by-cash-bank') {
      downloadPdf(`/treasury/reports/by-cash-bank/pdf?${params.toString()}`, 'Reporte_Por_Caja_Banco.pdf', 'Generando PDF por Caja/Banco...');
    } else if (activeTab === 'cb-movements' && selectedCbForReport) {
      downloadPdf(`/treasury/reports/cash-bank-movements/${selectedCbForReport}/pdf?${params.toString()}`, 'Movimientos_Caja_Banco.pdf', 'Generando PDF...');
    } else if (activeTab === 'cash-fc') {
      downloadPdf(`/treasury/reports/cash-paid-fiscal-invoices/pdf?${buildCashFcParams()}`, 'Compras_FC_Pagadas_Efectivo.pdf', 'Generando PDF...');
    }
  };
  
  const handleExportExcel = () => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    if (activeTab === 'ledger') {
      if (cashBankId) params.append('cash_bank_id', cashBankId);
      downloadPdf(`/treasury/reports/ledger/excel?${params.toString()}`, 'Libro_Diario_Tesoreria.xlsx', 'Generando Excel...');
    } else if (activeTab === 'by-concept') {
      downloadPdf(`/treasury/reports/by-concept/excel?${params.toString()}`, 'Reporte_Por_Concepto.xlsx', 'Generando Excel...');
    } else if (activeTab === 'by-cash-bank') {
      downloadPdf(`/treasury/reports/by-cash-bank/excel?${params.toString()}`, 'Reporte_Por_Caja_Banco.xlsx', 'Generando Excel...');
    } else if (activeTab === 'cb-movements' && selectedCbForReport) {
      downloadPdf(`/treasury/reports/cash-bank-movements/${selectedCbForReport}/excel?${params.toString()}`, 'Movimientos_Caja_Banco.xlsx', 'Generando Excel...');
    } else if (activeTab === 'cash-fc') {
      downloadPdf(`/treasury/reports/cash-paid-fiscal-invoices/excel?${buildCashFcParams()}`, 'Compras_FC_Pagadas_Efectivo.xlsx', 'Generando Excel...');
    }
  };
  
  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setCashBankId('');
    setConceptType('');
    setConceptId('');
    setMovementType('');
    setUserId('');
  };
  
  const allConcepts = [
    ...incomeConcepts.map(c => ({ ...c, type: 'income' })),
    ...expenseConcepts.map(c => ({ ...c, type: 'expense' }))
  ];
  
  const filteredConcepts = conceptType
    ? allConcepts.filter(c => c.type === conceptType)
    : allConcepts;

  return (
    <div className="space-y-6" data-testid="treasury-reports-page">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/treasury')} data-testid="back-to-treasury-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-indigo-600" />
              Reportes de Tesoreria
            </h1>
            <p className="text-muted-foreground mt-1">Informes avanzados con filtros y exportacion</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPdf} data-testid="export-pdf-btn" disabled={loading}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-excel-btn" disabled={loading}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>
      
      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <div>
              <Label className="text-xs">Desde</Label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            <div>
              <Label className="text-xs">Caja/Banco</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={cashBankId}
                onChange={e => setCashBankId(e.target.value)}
                data-testid="filter-cash-bank"
              >
                <option value="">Todas</option>
                {cashBanks.map(cb => (
                  <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Tipo Concepto</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={conceptType}
                onChange={e => { setConceptType(e.target.value); setConceptId(''); }}
                data-testid="filter-concept-type"
              >
                <option value="">Todos</option>
                <option value="income">Ingreso</option>
                <option value="expense">Egreso</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Concepto</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={conceptId}
                onChange={e => setConceptId(e.target.value)}
                data-testid="filter-concept"
              >
                <option value="">Todos</option>
                {filteredConcepts.map(c => (
                  <option key={c.concept_id} value={c.concept_id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Tipo Movimiento</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={movementType}
                onChange={e => setMovementType(e.target.value)}
                data-testid="filter-movement-type"
              >
                <option value="">Todos</option>
                <option value="collection">Cobranza</option>
                <option value="payment_order">Orden de Pago</option>
                <option value="adjustment">Ajuste</option>
                <option value="transfer_out">Transferencia Salida</option>
                <option value="transfer_in">Transferencia Entrada</option>
                <option value="check_deposit">Depósito Cheque</option>
                <option value="check_return">Cheque Rechazado</option>
                <option value="reconciliation_adjustment">Ajuste Conciliación</option>
                <option value="cash_count_adjustment">Ajuste Arqueo</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Usuario</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={userId}
                onChange={e => setUserId(e.target.value)}
                data-testid="filter-user"
              >
                <option value="">Todos</option>
                {users.map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={handleSearch} data-testid="apply-filters-btn" disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Cargando...' : 'Consultar'}
            </Button>
            <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">
              Limpiar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="ledger" className="flex items-center gap-1" data-testid="tab-ledger">
            <BookOpen className="w-4 h-4" />
            Libro Diario
          </TabsTrigger>
          <TabsTrigger value="by-concept" className="flex items-center gap-1" data-testid="tab-by-concept">
            <PieChart className="w-4 h-4" />
            Por Concepto
          </TabsTrigger>
          <TabsTrigger value="by-cash-bank" className="flex items-center gap-1" data-testid="tab-by-cash-bank">
            <Building2 className="w-4 h-4" />
            Por Caja/Banco
          </TabsTrigger>
          <TabsTrigger value="cb-movements" className="flex items-center gap-1" data-testid="tab-cb-movements">
            <FileText className="w-4 h-4" />
            Mov. Caja/Banco
          </TabsTrigger>
          <TabsTrigger value="cash-fc" className="flex items-center gap-1" data-testid="tab-cash-fc">
            <Banknote className="w-4 h-4" />
            Compras en Efectivo
          </TabsTrigger>
          <TabsTrigger value="payroll" className="flex items-center gap-1" data-testid="tab-payroll">
            <Users className="w-4 h-4" />
            Consulta de Nómina
          </TabsTrigger>
        </TabsList>
        
        {/* Libro Diario */}
        <TabsContent value="ledger" className="mt-4">
          {!ledgerData ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Seleccione filtros y presione <strong>Consultar</strong> para ver el Libro Diario</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase">Total Debe</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(ledgerData.totals.total_debit)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase">Total Haber</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(ledgerData.totals.total_credit)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase">Neto</p>
                    <p className={`text-xl font-bold ${ledgerData.totals.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(ledgerData.totals.net)}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Movimientos ({ledgerData.count})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[500px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Caja/Banco</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Referencia</TableHead>
                          <TableHead>Concepto</TableHead>
                          <TableHead>Descripcion</TableHead>
                          <TableHead className="text-right">Debe</TableHead>
                          <TableHead className="text-right">Haber</TableHead>
                          <TableHead className="text-right">Saldo</TableHead>
                          <TableHead>Usuario</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ledgerData.movements.map((m, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs whitespace-nowrap">{formatDateTime(m.created_at)}</TableCell>
                            <TableCell className="text-sm">{m.cash_bank_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {m.movement_type === 'collection' ? 'Cobranza' :
                                 m.movement_type === 'payment_order' ? 'Orden Pago' :
                                 m.movement_type === 'adjustment' ? 'Ajuste' :
                                 m.movement_type === 'transfer_out' ? 'Transf. Salida' :
                                 m.movement_type === 'transfer_in' ? 'Transf. Entrada' :
                                 m.movement_type === 'check_deposit' ? 'Depósito Cheque' :
                                 m.movement_type === 'check_return' ? 'Cheque Rechazado' :
                                 m.movement_type === 'reconciliation_adjustment' ? 'Ajuste Conciliación' :
                                 m.movement_type === 'cash_count_adjustment' ? 'Ajuste Arqueo' :
                                 m.movement_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{m.reference_number || '-'}</TableCell>
                            <TableCell className="text-sm">{m.concept_name || '-'}</TableCell>
                            <TableCell className="text-xs max-w-[150px] truncate">{m.description || '-'}</TableCell>
                            <TableCell className="text-right font-mono text-green-600">
                              {m.debit > 0 ? formatCurrency(m.debit) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-red-600">
                              {m.credit > 0 ? formatCurrency(m.credit) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono font-medium">
                              {formatCurrency(m.balance_after)}
                            </TableCell>
                            <TableCell className="text-xs">{m.created_by_name || '-'}</TableCell>
                          </TableRow>
                        ))}
                        {ledgerData.movements.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                              No se encontraron movimientos con los filtros seleccionados
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        
        {/* Por Concepto */}
        <TabsContent value="by-concept" className="mt-4">
          {!conceptData ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <PieChart className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Seleccione filtros y presione <strong>Consultar</strong> para ver el reporte por Concepto</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase flex items-center justify-center gap-1">
                      <ArrowUpCircle className="w-3 h-3 text-green-500" /> Total Ingresos
                    </p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(conceptData.totals?.total_incomes)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase flex items-center justify-center gap-1">
                      <ArrowDownCircle className="w-3 h-3 text-red-500" /> Total Egresos
                    </p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(conceptData.totals?.total_expenses)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase flex items-center justify-center gap-1">
                      {(conceptData.totals?.total_incomes - conceptData.totals?.total_expenses) >= 0 
                        ? <TrendingUp className="w-3 h-3 text-green-500" />
                        : <TrendingDown className="w-3 h-3 text-red-500" />
                      }
                      Resultado
                    </p>
                    <p className={`text-xl font-bold ${(conceptData.totals?.total_incomes - conceptData.totals?.total_expenses) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency((conceptData.totals?.total_incomes || 0) - (conceptData.totals?.total_expenses || 0))}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-green-700 flex items-center gap-2">
                      <ArrowUpCircle className="w-4 h-4" /> Ingresos (Cobranzas)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Concepto</TableHead>
                          <TableHead className="text-right">Ops</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(conceptData.incomes || []).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.concept_name}</TableCell>
                            <TableCell className="text-right">{r.count}</TableCell>
                            <TableCell className="text-right font-mono text-green-600">{formatCurrency(r.total_debit)}</TableCell>
                          </TableRow>
                        ))}
                        {(conceptData.incomes || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground py-4">Sin datos</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-red-700 flex items-center gap-2">
                      <ArrowDownCircle className="w-4 h-4" /> Egresos (Gastos)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Concepto</TableHead>
                          <TableHead className="text-right">Ops</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(conceptData.expenses || []).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.concept_name}</TableCell>
                            <TableCell className="text-right">{r.count}</TableCell>
                            <TableCell className="text-right font-mono text-red-600">{formatCurrency(r.total_credit)}</TableCell>
                          </TableRow>
                        ))}
                        {(conceptData.expenses || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground py-4">Sin datos</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
        
        {/* Por Caja/Banco */}
        <TabsContent value="by-cash-bank" className="mt-4">
          {!cashBankData ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Seleccione filtros y presione <strong>Consultar</strong> para ver el reporte por Caja/Banco</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase">Total Debe (Ingresos)</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(cashBankData.totals?.total_debit)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase">Total Haber (Egresos)</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(cashBankData.totals?.total_credit)}</p>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Detalle por Caja/Banco</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Caja/Banco</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Moneda</TableHead>
                        <TableHead className="text-right">Movimientos</TableHead>
                        <TableHead className="text-right">Debe</TableHead>
                        <TableHead className="text-right">Haber</TableHead>
                        <TableHead className="text-right">Neto</TableHead>
                        <TableHead className="text-right">Saldo Actual</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(cashBankData.cash_banks || []).map((r) => (
                        <TableRow key={r.cash_bank_id || r.cash_bank_name}>
                          <TableCell className="font-medium">{r.cash_bank_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {entityTypeLabel(r.entity_type)}
                            </Badge>
                          </TableCell>
                          <TableCell>{r.currency}</TableCell>
                          <TableCell className="text-right">{r.count}</TableCell>
                          <TableCell className="text-right font-mono text-green-600">{formatCurrency(r.total_debit)}</TableCell>
                          <TableCell className="text-right font-mono text-red-600">{formatCurrency(r.total_credit)}</TableCell>
                          <TableCell className={`text-right font-mono ${(r.total_debit - r.total_credit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(r.total_debit - r.total_credit)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold">{formatCurrency(r.current_balance)}</TableCell>
                        </TableRow>
                      ))}
                      {(cashBankData.cash_banks || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No se encontraron datos
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        
        {/* TAB MOV. POR CAJA/BANCO */}
        <TabsContent value="cb-movements" className="mt-6">
          <div className="space-y-4">
            {/* Selector de Caja/Banco */}
            <div className="flex flex-wrap items-end gap-3 p-3 bg-muted/30 rounded-lg border">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">Caja/Banco</Label>
                <select
                  value={selectedCbForReport}
                  onChange={(e) => setSelectedCbForReport(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  data-testid="cb-movements-select"
                >
                  <option value="">Seleccionar...</option>
                  {cashBanks.map(cb => (
                    <option key={cb.cash_bank_id} value={cb.cash_bank_id}>
                      {cb.name} ({cb.currency}) - Saldo: {formatCurrency(cb.current_balance)}
                    </option>
                  ))}
                </select>
              </div>
              <Button size="sm" onClick={handleSearch} disabled={!selectedCbForReport || loading} data-testid="btn-load-cb-movements">
                <Search className="w-4 h-4 mr-1" />
                Consultar
              </Button>
            </div>
            
            {cbMovementsData && (
              <div>
                <div className="flex justify-end gap-2 mb-3">
                  <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="btn-cb-movements-pdf">
                    <FileText className="w-4 h-4 mr-1" />PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="btn-cb-movements-excel">
                    <FileSpreadsheet className="w-4 h-4 mr-1" />Excel
                  </Button>
                </div>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      {cbMovementsData.cash_bank_name} — {cbMovementsData.movements?.length || 0} movimientos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Referencia</TableHead>
                          <TableHead>Concepto</TableHead>
                          <TableHead>Descripción</TableHead>
                          <TableHead className="text-right">Debe</TableHead>
                          <TableHead className="text-right">Haber</TableHead>
                          <TableHead className="text-right">Saldo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(cbMovementsData.movements || []).map((m, i) => (
                          <TableRow key={m.movement_id || i}>
                            <TableCell className="text-xs">{formatDate(m.date || m.created_at)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {m.movement_type === 'collection' ? 'Cobranza' :
                                 m.movement_type === 'payment_order' ? 'Orden de Pago' :
                                 m.movement_type === 'adjustment' ? 'Ajuste' :
                                 m.movement_type === 'transfer_out' ? 'Transf. Salida' :
                                 m.movement_type === 'transfer_in' ? 'Transf. Entrada' :
                                 m.movement_type === 'check_deposit' ? 'Depósito Cheque' :
                                 m.movement_type === 'check_return' ? 'Cheque Rechazado' :
                                 m.movement_type === 'reconciliation_adjustment' ? 'Ajuste Conciliación' :
                                 m.movement_type === 'cash_count_adjustment' ? 'Ajuste Arqueo' :
                                 m.movement_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">{m.reference_number}</TableCell>
                            <TableCell className="text-xs">{m.concept_name}</TableCell>
                            <TableCell className="text-xs">{m.description}</TableCell>
                            <TableCell className="text-right font-mono text-green-600">{m.debit > 0 ? formatCurrency(m.debit) : ''}</TableCell>
                            <TableCell className="text-right font-mono text-red-600">{m.credit > 0 ? formatCurrency(m.credit) : ''}</TableCell>
                            <TableCell className="text-right font-mono font-medium">{formatCurrency(m.balance_after)}</TableCell>
                          </TableRow>
                        ))}
                        {(!cbMovementsData.movements || cbMovementsData.movements.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                              No hay movimientos para el período seleccionado
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {!cbMovementsData && (
              <p className="text-center text-muted-foreground py-12">
                Seleccione una caja/banco y presione <strong>Consultar</strong> para ver los movimientos
              </p>
            )}
          </div>
        </TabsContent>

        {/* v3.37.0 — Compras en Efectivo (FC fiscales pagadas con efectivo) */}
        <TabsContent value="cash-fc" className="mt-4">
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-emerald-600" />
                  Filtros del reporte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">OP · Desde</Label>
                    <Input type="date" value={cashFcOpFrom} onChange={e => setCashFcOpFrom(e.target.value)} data-testid="cash-fc-op-from" />
                  </div>
                  <div>
                    <Label className="text-xs">OP · Hasta</Label>
                    <Input type="date" value={cashFcOpTo} onChange={e => setCashFcOpTo(e.target.value)} data-testid="cash-fc-op-to" />
                  </div>
                  <div>
                    <Label className="text-xs">FC · Desde</Label>
                    <Input type="date" value={cashFcFcFrom} onChange={e => setCashFcFcFrom(e.target.value)} data-testid="cash-fc-fc-from" />
                  </div>
                  <div>
                    <Label className="text-xs">FC · Hasta</Label>
                    <Input type="date" value={cashFcFcTo} onChange={e => setCashFcFcTo(e.target.value)} data-testid="cash-fc-fc-to" />
                  </div>
                  <div>
                    <Label className="text-xs">Proveedor</Label>
                    <select
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      value={cashFcSupplier}
                      onChange={e => setCashFcSupplier(e.target.value)}
                      data-testid="cash-fc-supplier"
                    >
                      <option value="">Todos</option>
                      {cashFcSuppliers.map(s => (
                        <option key={s.supplier_id} value={s.supplier_id}>
                          {s.business_name || s.trade_name || s.supplier_id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button onClick={loadCashFcReport} disabled={loading} data-testid="cash-fc-apply-btn">
                    <Search className="w-4 h-4 mr-2" />
                    {loading ? 'Cargando...' : 'Consultar'}
                  </Button>
                  <Button variant="ghost" onClick={clearCashFcFilters} data-testid="cash-fc-clear-btn">
                    Limpiar
                  </Button>
                  {cashFcData && (
                    <>
                      <Button variant="outline" onClick={handleExportPdf} data-testid="cash-fc-pdf-btn" disabled={loading}>
                        <FileText className="w-4 h-4 mr-2" />PDF
                      </Button>
                      <Button variant="outline" onClick={handleExportExcel} data-testid="cash-fc-excel-btn" disabled={loading}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Lista las facturas de compra fiscales canceladas (al menos parcialmente) con efectivo. Las fechas FC y OP son filtros independientes.
                </p>
              </CardContent>
            </Card>

            {!cashFcData ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Banknote className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Seleccione filtros y presione <strong>Consultar</strong> para ver el reporte</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card>
                    <CardContent className="pt-4 pb-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase">FCs</p>
                      <p className="text-xl font-bold">{cashFcData.totals?.fc_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase">OPs únicas</p>
                      <p className="text-xl font-bold">{cashFcData.totals?.op_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase">Total FCs</p>
                      <p className="text-xl font-bold text-indigo-600">{formatCurrency(cashFcData.totals?.fc_amount)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase">Pagado efectivo</p>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(cashFcData.totals?.cash_amount)}</p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardContent className="pt-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha OP</TableHead>
                          <TableHead>OP Nº</TableHead>
                          <TableHead>Caja origen</TableHead>
                          <TableHead>Proveedor</TableHead>
                          <TableHead>CUIT</TableHead>
                          <TableHead>Comprobante FC</TableHead>
                          <TableHead>Fecha FC</TableHead>
                          <TableHead className="text-right">Monto FC</TableHead>
                          <TableHead className="text-right">Efectivo (OP)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="cash-fc-rows">
                        {(cashFcData.rows || []).map((r, i) => (
                          <TableRow key={`${r.payment_order_id}-${r.purchase_invoice_id}-${i}`}>
                            <TableCell className="text-xs">{formatDate(r.op_date)}</TableCell>
                            <TableCell className="text-xs font-mono">{r.op_number || '-'}</TableCell>
                            <TableCell className="text-xs">{r.op_cash_bank_name}</TableCell>
                            <TableCell className="text-xs">{r.supplier_name}</TableCell>
                            <TableCell className="text-xs font-mono">{r.supplier_tax_id || '-'}</TableCell>
                            <TableCell className="text-xs font-mono">{r.fc_comprobante}</TableCell>
                            <TableCell className="text-xs">{formatDate(r.fc_date)}</TableCell>
                            <TableCell className="text-right font-mono text-indigo-700">{formatCurrency(r.fc_total)}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-700">{formatCurrency(r.op_cash_amount)}</TableCell>
                          </TableRow>
                        ))}
                        {(!cashFcData.rows || cashFcData.rows.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                              Sin resultados para los filtros aplicados
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Consulta de Nómina */}
        <TabsContent value="payroll" className="mt-4">
          <PayrollSearch embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TreasuryReports;
